import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useIntl } from "react-intl";
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { Button, Grid, Tooltip } from '@material-ui/core';
import WarningIcon from '../../icons/warningicon.svg'
import { withStyles } from '@material-ui/styles';
import PropTypes, { string } from "prop-types";
import { cloneDeep, forEach, isEqual, some, union } from 'lodash';
import {
  InfoOutlined as InfoOutlinedIcon,
} from '@material-ui/icons';

import { getI18n } from "../../../../utils/intl";
import {
  FilterByName,
} from './controls';
import {
  fetchCategoryTemplates,
  fetchPolicyTemplates,
  updateCategoryTemplates,
} from '../../../../actions/templateCategory';
import {
  getCategoryTemplates,
  getPolicyTemplates,
  getUpdateTemplatesResult,
} from '../../../../reducers/templateCategory';
import styles from './styles';
import ListContainer from '../../../list';
import {
  FormActionButtons,
  Switch,
} from '../../../../components';
import {
  KEY_ENTER,
  SUCCESS,
} from '../../../../constants';
import DropDownMenu from '../../../../components/DropDownMenu';
import { HeaderWithTooltip } from '../../../../pageComponents/HeaderWithTooltip';

export const TCTemplates = (props) => {
  const {
    categoryTemplates,
    categoryUuid,
    classes,
    currentTab,
    fetchCategoryTemplates,
    fetchPolicyTemplates,
    handleTemplatesChange,
    inTemplatesAddMode,
    isLoading = false,
    policyTemplates,
    setInTemplatesAddMode,
    updateCategoryTemplates,
    updateTemplatesResults,
  } = props;
  const { getI18nMessage } = getI18n(useIntl());
  const emptyFunction = () => {};
  const [currentTemplateRows, setCurrentTemplateRows] = useState([]);
  const [notificationStatus, setNotificationStatus] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [listRows, setListRows] = useState([]);
  const [nameSearchText, setNameSearchText] = useState('');
  const [filterText, setFilterText] = useState('');

  const isUnavailableTemplate = item => !item.name;

  useEffect(() => {
    if(policyTemplates) {
      setCurrentTemplateRows(cloneDeep(policyTemplates));
    }
  }, [policyTemplates]);

  useEffect(() => {
    if (currentTab === 'templates-tab' && categoryUuid) {
      setInTemplatesAddMode(false);
      fetchPolicyTemplates();
      fetchCategoryTemplates(categoryUuid);
    }
  }, [currentTab, categoryUuid]);

  useEffect(() => {
    if (updateTemplatesResults === SUCCESS) {
      clearNameFilter();
      if (categoryUuid) {
        fetchCategoryTemplates(categoryUuid);
      }
    }
  }, [updateTemplatesResults]);

  const clearNameFilter = () => {
    setFilterText('');
    setNameSearchText('');
  };
  const getTemplateFlexibility = (getI18nMessage) =>
    [
      {
        uuid: 'FIXED',
        name: getI18nMessage('label.category.details.templates.flexibility.fixed'),
      },
      {
        uuid: 'FLEXIBLE',
        name: getI18nMessage('label.category.details.templates.flexibility.flexible'),
      },
    ];
  const onFlexibilityChange = (val, uuid) => {
    const newRows = cloneDeep(listRows);
    newRows.forEach(item => {
      if (item.uuid === uuid) {
        const flexible = (val === 'FLEXIBLE');
        item.flexible = flexible;
        item.draggable = !flexible;
        item.droppable = !flexible;
      }
    });
    const sortedFixedList = newRows.filter(template => !template.flexible);
    const sortedFlexibleList = newRows.filter(template => template.flexible);
    setListRows(union(sortedFixedList, sortedFlexibleList));
  };
  const formatTemplateTypeDropDown = (flexible, uuid, getI18nMessage, disabled) =>
  (<div className={classes.roleDropdownContainer}>
    <DropDownMenu
      id="user-role-drop-down"
      data={getTemplateFlexibility(getI18nMessage)}
      disabled={disabled}
      value={flexible ? 'FLEXIBLE' : 'FIXED'}
      handleChange={(val) => onFlexibilityChange(val, uuid)}
    />
  </div>);

  const onSwitchToggle = (uuid, checked) => {
    const newRows = cloneDeep(listRows);
    newRows.forEach(item => {
      if (item.uuid === uuid) {
        item.isIncluded = checked;
      }
    });
    setListRows(newRows);
  };

  const hasUnavailableTemplates = useMemo(
      () => some(categoryTemplates, item => isUnavailableTemplate(item)), [categoryTemplates],
    );
  const getListColumns = ({ getI18nMessage }) => {
    return ([
      {
        format: (name) => (<span>{name}</span>),
        id: 'name',
        label: getI18nMessage('label.category.details.templates.title.name'),
        minWidth: 500,
        value: (item) => {
          if (isUnavailableTemplate(item)) {
            return (<>
              <Tooltip
                arrow placement="top"
                title={
                  getI18nMessage(
                  'label.api.edit.policy.templates.policy.template.unavailable.template.error.text',
                  )
                }
              >
                <span className={classes.unavailableTemplate}>
                  {getI18nMessage('label.category.details.templates.missing.template.name')}
                </span>
              </Tooltip>
              <span>{` ${getI18nMessage('label.id')}: ${item.uuid}`}</span>
            </>);
          }
          return <span>{item.name}</span>;
        },
      }, {
        format: ({ hasRouting } = {}) => (<span>{ hasRouting ? '✓' : '' }</span>),
        id: 'fragmentDetails',
        label: getI18nMessage('label.category.details.templates.title.routing'),
        minWidth: 80,
      }, {
        id: 'templatePosition',
        label: (
          <HeaderWithTooltip
            isTypeHeaderTooltip={false}
            classes={classes}
            label={getI18nMessage('label.category.details.templates.flexibility.position')}
            tooltipTitle={getI18nMessage('label.category.details.templates.flexibility.position.tooltip')}
          />
        ),
        minWidth: 100,
        isHidden : false,
        value: ({ flexible, uuid }) => formatTemplateTypeDropDown(flexible, uuid,
          getI18nMessage, hasUnavailableTemplates),
      }, {
        format: ({ isIncluded, name, uuid } = {}) => (<Switch
          disabled={hasUnavailableTemplates && !isUnavailableTemplate({ name })}
          id={`${uuid}-include`}
          checked={isIncluded}
          onChange={(checked)=> onSwitchToggle(uuid, checked)}
        />),
        id: 'id',
        label: getI18nMessage('label.category.details.templates.title.include'),
        minWidth: 80,
        value: (obj) => obj,
      },
    ]);
  };

  const isFormDirty = useMemo(() => {
    let hasIncludedTemplate = false;
    let hasUnincludedTemplate = false;
    listRows.forEach(item => {
      if (item.isIncluded) {
        hasIncludedTemplate = true;
      } else {
        hasUnincludedTemplate = true;
      }
    });
    let orderChanged = false;
    if (!inTemplatesAddMode && !hasUnincludedTemplate) {
      const currentIncludedTemplatesList = listRows.map(item => item.uuid);
      const actualTemplatesList = categoryTemplates ? categoryTemplates.map(item => item.uuid) : [];
      orderChanged = !isEqual(currentIncludedTemplatesList, actualTemplatesList);
    }
    let positionChanged = false;
    listRows.some(listTemplate => {
      const categoryTemplate = categoryTemplates.find(
          categoryTemplate => categoryTemplate.uuid === listTemplate.uuid);
      if(categoryTemplate && categoryTemplate.flexible !== listTemplate.flexible) {
        positionChanged = true;
        return true;
      }
    });
    return inTemplatesAddMode ? hasIncludedTemplate :
                      (hasUnincludedTemplate || orderChanged || positionChanged);
  }, [listRows, inTemplatesAddMode, categoryTemplates]);

  useEffect(() => {
    if (currentTab === 'templates-tab') {
      handleTemplatesChange(isFormDirty);
    }
  }, [isFormDirty]);

  const addButtonLabel = getI18nMessage('label.category.details.templates.add.templates.button');

  const onFilterByNameChange = (value) => {
    setNameSearchText(value);
  };
  const onNameSearchTextChanged = () => {
    setFilterText(nameSearchText);
  };
  const onFilterByNameKeyPress = (e) => {
    if (e.key === KEY_ENTER) {
      onNameSearchTextChanged();
    }
  };

  const disableAddButton = isFormDirty || hasUnavailableTemplates;

  const renderFilterAndSortContent = () => (
    <Fragment>
      <Grid className={classes.controlsContainer} item md={6} sm={6} xs={12}>
        <FilterByName
          fieldContainerClass={classes.nameFieldContainer}
          handleBlur={onNameSearchTextChanged}
          handleChange={onFilterByNameChange}
          name={getI18nMessage('label.filter')}
          onKeyPress={onFilterByNameKeyPress}
          placeholder={getI18nMessage('label.category.details.templates.title.name')}
          value={nameSearchText}
        />
      </Grid>
      <Grid item md={6} sm={6} xs={12}>
      {!inTemplatesAddMode && <Button
          className={classes.addBtn}
          disabled={disableAddButton}
          variant="contained" color="secondary"
          onClick={onAddTemplates}
          id={'category-templates-add'}
          data-apim-test={'category-templates-add'}
          data-layer7-test={'category-templates-add'}
        >
          {addButtonLabel}
        </Button>}
      </Grid>
    </Fragment>
  );

  useEffect(() => {
    if (inTemplatesAddMode) {
      const categoryTemplatesMap = {};
      categoryTemplates && categoryTemplates.map(item => {
        categoryTemplatesMap[item.uuid] = item;
      });
      const templatesCanBeAdd = [];
      currentTemplateRows.map((item) => {
        if (!categoryTemplatesMap[item.uuid]) {
          const cloneItem = cloneDeep(item);
          cloneItem.isIncluded = false;
          templatesCanBeAdd.push(cloneItem);
        }
      });
      return setListRows(templatesCanBeAdd);
    } else {
      const alreadyAddedTemplates = [];
      forEach(categoryTemplates, (template) => {
        const newTemplate = cloneDeep(template);
        newTemplate.name = newTemplate.name || '';
        newTemplate.isIncluded = true;
        newTemplate.draggable = !newTemplate.flexible;
        newTemplate.droppable = !newTemplate.flexible;
        alreadyAddedTemplates.push(newTemplate);
      });
      const sortedFixedList = alreadyAddedTemplates.filter(template => !template.flexible);
      const sortedFlexibleList = alreadyAddedTemplates.filter(template => template.flexible);
      setListRows(union(sortedFixedList, sortedFlexibleList));
    }
  }, [categoryTemplates, currentTemplateRows, inTemplatesAddMode]);

  const onAddTemplates = () => {
    clearNameFilter();
    if (!inTemplatesAddMode) {
      setInTemplatesAddMode(true);
    }
  };

  const listColumns = getListColumns({ getI18nMessage, classes });

  const onSave = () => {
    let selectedTemplates = listRows.filter(item => item.isIncluded);
    if (inTemplatesAddMode) {
      selectedTemplates = union(categoryTemplates, selectedTemplates);
    }
    const fixedTemplateUuids =
          selectedTemplates.filter(template => !template.flexible).map(template => template.uuid);
    const flexibleTemplateUuids =
    selectedTemplates.filter(template => template.flexible).map(template => template.uuid);
    updateCategoryTemplates(categoryUuid, fixedTemplateUuids, flexibleTemplateUuids);
  };

  const normalModeCancelHandler = () => {
    fetchPolicyTemplates();
    fetchCategoryTemplates(categoryUuid);
  };

  const addModeCancelHandler = () => {
    clearNameFilter();
    setInTemplatesAddMode(false);
    fetchPolicyTemplates();
    fetchCategoryTemplates(categoryUuid);
  };

  const onCancel = inTemplatesAddMode ? addModeCancelHandler : normalModeCancelHandler;

  const filteredListRows = filterText ? listRows.filter(
      item => item && item.name && item.name.toUpperCase().includes(filterText.toUpperCase()),
    ) : listRows;

  let assistText = '';
  if (inTemplatesAddMode) {
    assistText = (<div className={classes.assistTextContainer}>
      <div className={classes.assistTextIcon}><InfoOutlinedIcon /></div>
      <div className={classes.assistText}>
        {getI18nMessage('label.category.details.templates.footer.help.text')}
      </div>
    </div>);
  }

  const selectAllMissingTemplates = () => {
    const newRows = cloneDeep(listRows);
    newRows.forEach(item => {
      if (isUnavailableTemplate(item)) {
        item.isIncluded = false;
      }
    });
    setListRows(newRows);
  };

  
  const onDragAndDrop = (dragIndex, hoverIndex) => {
    const newRows = cloneDeep(listRows);
    const movingRow = newRows[dragIndex];
    newRows.splice(dragIndex, 1);
    newRows.splice(hoverIndex, 0 , movingRow);
    setListRows(newRows);
  }

  const renderUnavailableTemplatesMessage = () => (
    <div>
      <div className={classes.cautionMessageDiv}>
        <img
          src={WarningIcon}
          className={classes.missingTemplatesIcon}
          alt="Missing Tempaltes Warning"
        />
        {getI18nMessage('label.category.details.templates.missing.template.message')}
        {getI18nMessage('label.category.details.templates.missing.template.message.btn.1')}
        <Button
          color="primary"
          onClick={selectAllMissingTemplates}
        >
          {getI18nMessage('label.category.details.templates.missing.template.message.btn.2')}
        </Button>
      </div>
    </div>
  );

  const showFooter = isFormDirty || inTemplatesAddMode;

  return (
    <Fragment>
      {hasUnavailableTemplates && renderUnavailableTemplatesMessage()}
        <ListContainer
          addButtonLabel={''}
          columns={listColumns}
          filterAndSortContent={renderFilterAndSortContent()}
          hasPaginationDisabled={true}
          hidePagination={true}
          isLoading={isLoading}
          listPageId="templates-list"
          noResultsMessage={getI18nMessage('label.request.list.no.results')}
          notificationId="category-templates-notifications"
          notificationMessage={notificationMessage}
          notificationStatus={notificationStatus}
          notifyMessages={emptyFunction}
          onAdd={false}
          pageClass={classes.pageClass}
          pageFilterAndSortClass={classes.pageFilterAndSortClass}
          pageHeaderDescription={''}
          pageHeaderTitle={''}
          rows={filteredListRows}
          setNotificationMessage={setNotificationMessage}
          setNotificationStatus={setNotificationStatus}
          showListHeader={true}
          allowOrdering={!inTemplatesAddMode && !filterText && !hasUnavailableTemplates}
          onDragAndDrop={onDragAndDrop}
        />
        {showFooter && <FormActionButtons
          assistText={assistText}
          isContainerFooter
          isDisabled={!isFormDirty}
          nextText={getI18nMessage('label.save.button')}
          onNextClick={onSave}
          onCancelClick={onCancel}
          id="category-templates-form-buttons"
        />}
    </Fragment>
  );
};
  
const mapStateToProps = (state) => ({
  categoryTemplates: getCategoryTemplates(state),
  policyTemplates: getPolicyTemplates(state),
  updateTemplatesResults: getUpdateTemplatesResult(state),
});

const mapDispatchToProps = {
  fetchCategoryTemplates,
  fetchPolicyTemplates,
  updateCategoryTemplates,
};

TCTemplates.propTypes = {
  categoryTemplates: PropTypes.array,
  categoryUuid: PropTypes.string,
  classes: PropTypes.object,
  currentTab: PropTypes.string,
  fetchCategoryTemplates: PropTypes.func,
  fetchPolicyTemplates: PropTypes.func,
  handleTemplatesChange: PropTypes.func,
  inTemplatesAddMode: PropTypes.bool,
  isLoading: PropTypes.bool,
  policyTemplates: PropTypes.array,
  setInTemplatesAddMode: PropTypes.func,
  updateCategoryTemplates: PropTypes.func,
  updateTemplatesResults: PropTypes.func,
  label: string,
};

TCTemplates.displayName = 'TCTemplates';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(TCTemplates);
